import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { type ReactElement, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { usePartnerPlanUrl } from 'partner360/components/partnerNameWithLink'
import { FINANCIAL_SECTION_URL_VALUE, INITIATIVES_SECTION_URL_VALUE } from 'partner360/configs'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  getAttentionStatusActions,
  getAttentionStatusRole,
  isPlanCountedAttentionTitle,
  type TAttentionStatusActionId,
} from 'partner360/entities/attentionStatuses'
import { NEW_VIEW_MODE_URL_PARAM_VALUE } from 'partner360/hooks/viewModeUrlParam'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import {
  openCockpitAttentionPartner,
  setCockpitSelectedAttentionStatus,
} from '../../../../model/financialCockpitSlice'
import {
  selectCockpitAttentionPartners,
  selectCockpitAttentionStatusCatalog,
  selectCockpitAttentionStatusRaises,
  selectCockpitCountsPlansByPartner,
  selectCockpitOffering,
  selectCockpitUserAccess,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { CockpitPartnerScope } from '../../../components/cockpitPartnerScope'
import type { IAttentionStatusesItem } from '../../types'

import { AttentionNextStepRow } from './AttentionNextStepRow'
import { AttentionNextStepsGroup } from './AttentionNextStepsGroup'
import { NEXT_STEPS_LIMIT } from './constants'
import { getStepInitials } from './getStepInitials'
import { ManagerAcknowledgmentStep } from './ManagerAcknowledgmentStep'
import { ManagerOwnSignStep } from './ManagerOwnSignStep'
import { ManagerPbmReminderStep } from './ManagerPbmReminderStep'
import { ManagerSobEntry } from './ManagerSobEntry'
import { ManagerSobReminderStep } from './ManagerSobReminderStep'
import { usePartnerBaselines } from './usePartnerBaselines'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps
const SHORT = KEYS.short

const ICON_PLAN = 'kbase kbase-document'
const ICON_EDIT = 'kbase kbase-edit'
const ICON_GO = 'kbase kbase-arrow-right'

interface IManagerStepKind {
  /** The act the role catalog has to grant for the step to offer its button. */
  actionId: TAttentionStatusActionId
  labelKey: string
  /** The word the step's button says before its icon, and the icon: where the act takes the
   *  reader, so the button reads without its tooltip. */
  shortLabelKey: string
  icon: string
  /** The plan's own signers decide the step, as on the partner's cockpit: the reader's row signs
   *  right here, every other reader reminds the signer the plan waits for. */
  bySigners?: boolean
  /** A PBM enters the partner's SOB actuals right here, in the partner's own SOB dialog. */
  entersSob?: boolean
  /** The PBM's own act opens the partner's plan rather than the partner's status in the tile. */
  pbmOpensPlan?: boolean
  /** Every reader but the PBM whom the catalog lets remind reminds the PBM, with the plan beside. */
  remindsPbm?: boolean
  /** A plan held only by its first PBM signature stays raised here, so the reader whose row the
   *  plan waits for signs it here instead. */
  signsOwnRow?: boolean
  /** The plan's tab the step's plan opens on; without one the plan opens on its own first view. */
  tab?: string
}

/** A status the PBM works in the partner's status detail, as on the partner's own cockpit. */
const pbmDetailStep = (
  actionId: TAttentionStatusActionId,
  shortLabelKey: string,
  icon: string,
  tab?: string
): IManagerStepKind => ({
  actionId,
  labelKey: ATTENTION_STATUS_ACTIONS[actionId].labelKey,
  shortLabelKey,
  icon,
  remindsPbm: true,
  tab,
})

/** The statuses a partner's step answers on the manager cockpit, and what that step is called. */
const MANAGER_STEP_KINDS: Record<number, IManagerStepKind> = {
  [ATTENTION_STATUS_BITS.planned]: {
    actionId: 'goToSection',
    labelKey: KEYS.completePlan,
    shortLabelKey: SHORT.plan,
    icon: ICON_PLAN,
    pbmOpensPlan: true,
    remindsPbm: true,
    signsOwnRow: true,
  },
  [ATTENTION_STATUS_BITS.sobActuals]: {
    actionId: 'updateSob',
    labelKey: KEYS.submitSob,
    shortLabelKey: SHORT.sob,
    icon: ICON_EDIT,
    entersSob: true,
    remindsPbm: true,
  },
  [ATTENTION_STATUS_BITS.acknowledgment]: {
    actionId: 'acknowledge',
    labelKey: ATTENTION_STATUS_ACTIONS.acknowledge.labelKey,
    shortLabelKey: SHORT.sign,
    icon: 'kbase kbase-check',
    bySigners: true,
  },
  [ATTENTION_STATUS_BITS.finSobQuality]: pbmDetailStep(
    'openQualityReview',
    SHORT.review,
    ICON_GO,
    FINANCIAL_SECTION_URL_VALUE
  ),
  [ATTENTION_STATUS_BITS.initiativesQuality]: pbmDetailStep(
    'openQualityReview',
    SHORT.review,
    ICON_GO,
    INITIATIVES_SECTION_URL_VALUE
  ),
  [ATTENTION_STATUS_BITS.mdf]: pbmDetailStep(
    'createInitiative',
    SHORT.initiative,
    ICON_EDIT,
    INITIATIVES_SECTION_URL_VALUE
  ),
  [ATTENTION_STATUS_BITS.programRisk]: pbmDetailStep(
    'createInitiative',
    SHORT.initiative,
    ICON_EDIT,
    INITIATIVES_SECTION_URL_VALUE
  ),
  [ATTENTION_STATUS_BITS.quarterPlan]: pbmDetailStep(
    'provideJustification',
    SHORT.justify,
    ICON_EDIT,
    FINANCIAL_SECTION_URL_VALUE
  ),
}

export const hasManagerNextSteps = (bitValue: number | undefined) =>
  bitValue !== undefined && bitValue in MANAGER_STEP_KINDS

interface IManagerStep {
  partnerId: string
  partnerName: string
  country: string
  /** The offering the step is for, where the partner's raise names one. */
  offering: string | null
  /** Every offering the partner raised the status for, in the payload's order. */
  raisedOfferings: string[]
  baseline: number
}

interface IManagerPartnerStepProps {
  step: IManagerStep
  item: IAttentionStatusesItem
  kind: IManagerStepKind
  /** The role may take the status' act; otherwise the step only opens the status' partners. */
  canAct: boolean
  /** The role may remind the partner's PBM. */
  canRemindPbm: boolean
  isPbm: boolean
  showOffering: boolean
  onSelect: (id: string) => void
  onOpenPartnerDetail?: () => void
}

const ManagerPartnerStep = ({
  step,
  item,
  kind,
  canAct,
  canRemindPbm,
  isPbm,
  showOffering,
  onSelect,
  onOpenPartnerDetail,
}: IManagerPartnerStepProps) => {
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const deckOffering = useSelector(selectCockpitOffering)
  const offering = step.offering ?? (deckOffering === ProgramTypes.Overall ? null : deckOffering)
  /* The partner opens in the planning view this tile is part of, on the offering the step is for. */
  const planUrl = usePartnerPlanUrl({
    partnerId: step.partnerId,
    fYear,
    programType: offering ?? undefined,
    viewMode: NEW_VIEW_MODE_URL_PARAM_VALUE,
    tabUrl: kind.tab,
  })

  /* The SOB dialog is one offering's, so a partner that raised the status for several opens on
     the first of them. */
  const sobOffering = offering ?? step.raisedOfferings[0] ?? null
  const entersSob = canAct && isPbm && kind.entersSob === true && sobOffering !== null

  /* A PBM works the partner's status in the tile, as picking the partner in the status' list
     does, except where the step is the plan itself; every other role opens the partner's plan. */
  const openPartnerDetail = () => {
    dispatch(setCockpitSelectedAttentionStatus({ bitValue: Number(item.bitValue), offering }))
    dispatch(openCockpitAttentionPartner({ partnerId: step.partnerId, offering }))
    onOpenPartnerDetail?.()
  }

  const openPlan = () => window.open(planUrl, '_blank', 'noreferrer')

  const handleAct = () => {
    if (!isPbm || kind.pbmOpensPlan) {
      openPlan()
      return
    }

    openPartnerDetail()
  }

  const row = {
    mark: { kind: 'category' as const, text: getStepInitials(step.partnerName) },
    name: step.partnerName,
    fact: (
      <>
        {showOffering && step.offering && (
          <>
            <Translation span text={getOfferingTitleKey(step.offering)} />
            {step.country ? ' · ' : ''}
          </>
        )}
        {step.country}
      </>
    ),
    tone: 'go' as const,
    severity: item.severity,
    labelKey: canAct ? kind.labelKey : KEYS.seeDetails,
    shortLabelKey: canAct ? kind.shortLabelKey : SHORT.details,
    icon: canAct ? kind.icon : 'kbase kbase-chevron-right',
  }

  if (kind.bySigners) {
    return (
      <ManagerAcknowledgmentStep
        partnerId={step.partnerId}
        offering={offering}
        mark={row.mark}
        name={row.name}
        fact={row.fact}
        item={item}
        onSelect={onSelect}
        onOpenPlan={isPbm ? undefined : openPlan}
      />
    )
  }

  const remindsPbm = kind.remindsPbm === true && !isPbm && canRemindPbm

  const ownAct = (): ReactElement => {
    if (remindsPbm && kind.entersSob && sobOffering) {
      return (
        <CockpitPartnerScope
          partnerId={step.partnerId}
          partnerName={step.partnerName}
          status={{ bitValue: Number(item.bitValue), offering: sobOffering }}
        >
          <ManagerSobReminderStep
            row={row}
            partnerId={step.partnerId}
            offering={sobOffering}
            bitValue={Number(item.bitValue)}
          />
        </CockpitPartnerScope>
      )
    }

    if (remindsPbm && !kind.entersSob) {
      return (
        <ManagerPbmReminderStep
          row={row}
          partnerId={step.partnerId}
          offering={offering}
          bitValue={Number(item.bitValue)}
          onOpenPlan={openPlan}
        />
      )
    }

    if (entersSob && sobOffering) {
      return (
        <CockpitPartnerScope
          partnerId={step.partnerId}
          partnerName={step.partnerName}
          status={{ bitValue: Number(item.bitValue), offering: sobOffering }}
        >
          <ManagerSobEntry row={row} offering={sobOffering} onFallback={openPartnerDetail} />
        </CockpitPartnerScope>
      )
    }

    return <AttentionNextStepRow {...row} onClick={canAct ? handleAct : () => onSelect(item.id)} />
  }

  if (kind.signsOwnRow && offering) {
    return (
      <ManagerOwnSignStep
        row={row}
        partnerId={step.partnerId}
        offering={offering}
        onOpenPlan={isPbm ? undefined : openPlan}
      >
        {ownAct()}
      </ManagerOwnSignStep>
    )
  }

  return ownAct()
}

interface IManagerNextStepsProps {
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
  /** Called once a PBM's step opened the partner's status, so a collapsed tile expands to it. */
  onOpenPartnerDetail?: () => void
}

/**
 * A status of the manager cockpit and the partners to start with: the ones with the largest
 * last-year sales, each with the status' act. On `Overall` a status counted in plans lists each
 * partner's offering apart, as it counts them. The role catalog decides whether the act is
 * offered; a role without it opens the status' partners instead. Acknowledgment is decided by the
 * plan's signers instead of the catalog.
 */
export const ManagerNextSteps = ({
  item,
  onSelect,
  onOpenPartnerDetail,
}: IManagerNextStepsProps) => {
  const bitValue = Number(item.bitValue)
  const kind = MANAGER_STEP_KINDS[bitValue]
  const raises = useSelector(selectCockpitAttentionStatusRaises)
  const partners = useSelector(selectCockpitAttentionPartners)
  const catalog = useSelector(selectCockpitAttentionStatusCatalog)
  const deckOffering = useSelector(selectCockpitOffering)
  const { role } = useSelector(selectCockpitUserAccess)
  const getBaseline = usePartnerBaselines()

  const statusRole = getAttentionStatusRole(role)
  const roleActions = getAttentionStatusActions(bitValue, statusRole)
  const canAct = roleActions.some(({ id }) => id === kind.actionId)
  const canRemindPbm = roleActions.some(({ id }) => id === 'remindPbm')
  const isPbm = statusRole === 'pbm'
  const remindsPbm = kind.remindsPbm === true && !isPbm && canRemindPbm
  /* A distributor plans all its offerings as one, so its partner is one step. */
  const countsPlansByPartner = useSelector(selectCockpitCountsPlansByPartner)
  const isPlanCounted =
    !countsPlansByPartner &&
    isPlanCountedAttentionTitle(catalog.find(({ BitValue }) => BitValue === bitValue)?.Title ?? '')

  const steps = useMemo<IManagerStep[]>(() => {
    const byId = new Map(partners.map((partner) => [partner.partnerId, partner]))

    return (raises.get(bitValue) ?? [])
      .flatMap(({ partnerId, offerings }) => {
        const partner = byId.get(partnerId)

        if (!partner) {
          return []
        }

        const stepOfferings: (string | null)[] =
          isPlanCounted && offerings.length > 1
            ? offerings
            : [offerings.length === 1 ? offerings[0] : null]

        return stepOfferings.map((offering) => ({
          partnerId,
          partnerName: partner.partnerName,
          country: partner.country,
          offering,
          raisedOfferings: offerings,
          baseline: getBaseline(partnerId, offering),
        }))
      })
      .sort(
        (left, right) =>
          right.baseline - left.baseline || left.partnerName.localeCompare(right.partnerName)
      )
  }, [bitValue, getBaseline, isPlanCounted, partners, raises])

  const shown = steps.slice(0, NEXT_STEPS_LIMIT)

  return (
    <AttentionNextStepsGroup
      item={item}
      onSelect={onSelect}
      restCount={steps.length - shown.length}
      hasDetailStep={!canAct && !remindsPbm && !kind.bySigners && shown.length > 0}
    >
      {shown.length === 0
        ? null
        : shown.map((step) => (
            <ManagerPartnerStep
              key={`${step.partnerId}|${step.offering ?? ''}`}
              step={step}
              item={item}
              kind={kind}
              canAct={canAct}
              canRemindPbm={canRemindPbm}
              isPbm={isPbm}
              showOffering={deckOffering === ProgramTypes.Overall}
              onSelect={onSelect}
              onOpenPartnerDetail={onOpenPartnerDetail}
            />
          ))}
    </AttentionNextStepsGroup>
  )
}

ManagerNextSteps.displayName = 'ManagerNextSteps'
